import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { BagErrorModel } from '@models/api-models/bag-error-model';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@shared/app-state';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'icon-bag-error',
    styleUrls: ['./icon-bag-error.component.scss'],
    template: `
		<ng-container>
		  @if (state === 'collapsed') {
		    <i
		      class="fa-light fa-exclamation fa-lg "
		      [ngClass]="{ 'error-header-icon': state === 'collapsed' }"
		    ></i>
		  }
		  @if (state === 'expanded') {
		    <i
		      class="fa-light fa-close fa-lg "
		      [ngClass]="{ 'error-header-icon-close': state === 'expanded' }"
		    ></i>
		  }
		  <div [ngClass]="{'no-display': state ==='collapsed' }" class="content-wrapper" [@displayAnim]="state">
		    <div class="header">
		      <i class="fa-light fa-exclamation-circle fa-lg"></i>
		      <span>{{ 'BAG.ERROR.HEADER' | translate }}</span>
		    </div>
		    <div class="description">
		      <span>{{ 'BAG.ERROR.DESCRIPTION' | translate }}</span>
		    </div>
		    <ul>
		      @for (error of visibleErrors(); track error) {
		        <li>
		          <div class="error-item">
		            <span class="title ">
		              <span
		                class="highlighted-text-weight highlighted-color"
		                >{{ error.FirstWordInTitle }}</span
		                >
		                {{ error.Title }}</span
		                >
		                <i
		                  class="fa-light fa-close fa-lg highlighted-color"
		                  (click)="removeError($event, error.Id)"
		                ></i>
		              </div>
		            </li>
		          }
		        </ul>
		        <div class="footer">
		          <span
		            class="highlighted-color highlighted-text-weight"
		            (click)="goToBag()"
		            >{{ 'BAG.ERROR.GOTOBAG' | translate }}
		            <i class="fa-light fa-arrow-right fa-lg"></i
		            ></span>
		            <span
		              class="highlighted-color highlighted-text-weight"
		              (click)="clearAllErrors($event)"
		              >{{ 'BAG.ERROR.CLEARALL' | translate }}
		              <i class="fa-light fa-close fa-lg"></i
		              ></span>
		            </div>
		          </div>
		        </ng-container>
		`,
    imports: [TranslatePipe, CommonModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('displayAnim', [
            state('collapsed', style({
                opacity: '0',
            })),
            state('expanded', style({
                opacity: '1',
            })),
            transition('collapsed => expanded', [animate('0.2s')]),
            transition('expanded => collapsed', [animate('0.2s')]),
        ]),
    ]
})
export class IconBagErrorComponent implements OnInit {
	@Input() showErrorItems: BehaviorSubject<boolean>;
	public bagErrors: BagErrorModel[] = [];
	public state: 'collapsed' | 'expanded' = 'collapsed';

	constructor(
		private appState: AppState,
		private elementRef: ElementRef,
		private router: Router
	) {}

	//close list of errors when clicking outside of the icon
	@HostListener('document:click', ['$event']) onDocumentClick(
		event: MouseEvent
	) {
		const newLocal = event.target as Node;
		if (!this.elementRef.nativeElement.contains(newLocal)) {
			this.showErrorItems.next(false);
		}
	}

	ngOnInit() {
		this.appState.bagErrors.subscribe((errors) => {
			this.bagErrors = errors;
		});

		this.showErrorItems.subscribe((show) => {
			this.state = show ? 'expanded' : 'collapsed';
		});
	}

	visibleErrors(): BagErrorModel[] {
		//Showing only 10 latest errors
		return this.bagErrors
			.slice(-10)
			.reverse()
			.map((x) => {
				const regex = /^(\S+)\s*(.*)$/;
				const matches = x.Title.match(regex);
				const response: BagErrorModel = {
					Title: matches?.[2] || '',
					Id: x.Id,
					FirstWordInTitle: matches?.[1],
				};
				return response;
			});
	}

	goToBag() {
		this.state = 'collapsed';
		this.router.navigate(['/bag']);
	}

	removeError(event: MouseEvent, id: string) {
		event.stopPropagation();
		const updatedErrors = this.bagErrors.filter((error) => error.Id !== id);

		if (this.appState.bagErrors.value.length === 1) {
			this.showErrorItems.next(false);

			setTimeout(() => {
				this.appState.bagErrors.next([]);
			}, 200);
		} else {
			this.appState.bagErrors.next(updatedErrors);
		}
	}

	clearAllErrors(event: MouseEvent) {
		event.stopPropagation();
		this.showErrorItems.next(false);

		setTimeout(() => {
			this.appState.bagErrors.next([]);
		}, 200);
	}
}
